import type { ReactElement } from 'react';
import type { IReportType } from 'app/services/reportsBoardApi';
import type { GenerateState } from '../generateReport';
import AhuModelTab from './AhuModelTab';
import EwheelSelectionTab from './EwheelSelectionTab';
import FanDataTab from './FanDataTab';
import PhxSelectionTab from './PhxSelectionTab';
import {
  CmsDocumentTab,
  EquipmentListTab,
  EquipmentScheduleTab,
  PricingTab,
  SalesTermsTab,
  ScopeOfSupplyTab,
  SubmittalCoverTab,
} from './DocumentReportTabs';
import {
  ProposalTab,
  SalesOrderTab,
  ServiceOrderTab,
  ShippingOrderTab,
} from './OrderReportTabs';
import {
  CoilAnalyticsTab,
  IntegratedProjectLedgerTab,
  SubmittalPackageTab,
  UsageStatisticsTab,
} from './SubmittalUsageLedgerTabs';
import { CaActivitiesTab, CaMaterialsTab, CaSummaryTab } from './CostAccountingTabs';
import {
  OpenProjectsTab,
  OrderAcknowledgmentTab,
  ProjectListTab,
  TimelineTab,
} from './ManagementScheduleTabs';
import {
  BaseCutListTab,
  BeltDriveSelectTab,
  BillOfLadingTab,
  CasingWallCutListTab,
  EngHoursTab,
  FloorCutListTab,
  FreightRequestTab,
  MechDwgTab,
  MfgPackageTab,
  PartsByOthersListTab,
  PlantHoursTab,
  RoofCutListTab,
  TemperatureProfileTab,
} from './StubReportTabs';

/**
 * Phase 5c — the generate-only report tab panes, ported 1:1 from the legacy
 * tab templates. These reports have NO in-tab result render: their Backbone
 * views POST the generate request and, on POST success, re-render the SAME
 * static shell (header + "generation in progress" preloader) — the finished
 * file arrives later through the header notifications poll. Sources:
 *
 *  - tabs/_2d_drawings/view/_2d_drawing.js:56-71 + tmpl/_2d_drawing.tmpl
 *    (one shared view for the six drawing values, app.js:170-176)
 *  - tabs/pressure_drop_calc/{view,tmpl} (:31-37 render, tmpl:12 structure div)
 *  - tabs/electrical_calc/{view,tmpl} (:39-46, tmpl:12)
 *  - tabs/weight_calc/{view,tmpl} (:37-43, tmpl:12 — div id="weight_structure")
 *  - tabs/center_of_gravity/{view,tmpl} (:30-36, tmpl:12)
 *  - tabs/bill_of_materials/{view,tmpl} (:36-41; its container-header nests an
 *    extra col-xs-12 and the report-name carries a leading space, tmpl:1-7)
 *  - tabs/mfs_profile_cut_list/{view,tmpl} (:34-40; a DIFFERENT shell — group
 *    "Manufacturing " + an empty .time-stamp span and a bare dotted preloader,
 *    no "Report generation in progress..." container, tmpl:1-13)
 *
 * Dead legacy machinery deliberately NOT ported (nothing ever reaches it):
 *  - weight_calc/bill_of_materials report.ahu/report.project.tmpl tables and
 *    mfs report.ahu.tmpl: rendered only from renderReport/openGeneratedReport
 *    when `this.data[state]` is set — but every socket block that would set it
 *    is commented out (weight_calc.js:123-138, mfs_profile_cut_list.js:149-163),
 *    so the tables never render in the live legacy app.
 *  - export_csv flows (weight_calc.js:201-253, mfs_profile_cut_list.js:69-128)
 *    and the electrical_calc/bill_of_materials `export_csv` listeners (which
 *    point at methods that don't even exist, electrical_calc.js:29-33 /
 *    bill_of_materials.js:32): the only trigger is the header's
 *    `click #export_csv` binding (main_header.js:62), and main_header.tmpl no
 *    longer renders an #export_csv/#print/#export_pdf button — the chain is
 *    unreachable. CSV/PDF delivery happens via the Recent-Reports dropdown.
 *  - pressure_drop_calc viewPDF/export_pdf (:95-149): not in any events hash
 *    and no 'export_pdf:report:pressure_drop_calc' listener is registered —
 *    dead code.
 *
 * Render timing: the legacy pane is EMPTY until the generate POST succeeds
 * (success → view.render(), e.g. weight_calc.js:114-116); the page passes
 * `rendered` from the kit's onTabRendered (POST resolved) to reproduce that,
 * and the shell then stays until the 5s revert hides the pane
 * (action.js:176-180). `faded` is the 4.5s 'opacity-0' on the
 * .generation-in-progress container (action.js:124-128) — the MFS pane has no
 * such container, so it ignores it, exactly like the legacy DOM.
 */
export interface GeneratedTabPaneProps {
  report: IReportType;
  /** 4.5s opacity-0 fade (action.js:124-128). */
  faded: boolean;
  /** Generate POST resolved — the legacy success→render() moment. */
  rendered: boolean;
  /** 5d: the dispatch states of the current generate run, in dispatch order
   *  (one per 'generate:report:<name>' event) — the data panes fetch/render
   *  per state like the legacy views. */
  states?: GenerateState[];
  /** 5d: 'reopen' when re-showing a cached generated report — the legacy
   *  open:generated_report path (action.js:240-249). Default 'generate'. */
  mode?: 'generate' | 'reopen';
  /** 5d: the cache key (reportTabData) for reopen mode. */
  reopenKey?: string | null;
}

type PaneComponent = (props: GeneratedTabPaneProps) => ReactElement;

/**
 * 5d: tabs whose legacy view renders its shell SYNCHRONOUSLY at dispatch
 * time instead of on a POST success — filter_data renders in initialize
 * (filter_data.js:22-24), er_wheel_selection and ahu_model call render()
 * first thing in generateReport (er_wheel_selection.js:147-149,
 * ahu_model.js:65-67). The page marks these rendered (and clears the tabs
 * preloader, which their render() does via
 * `$('.report-tabs-preloader').removeClass('active')`) at onDispatched.
 */
export const IMMEDIATE_RENDER_TABS = new Set([
  'filter_data',
  'er_wheel_selection',
  'ahu_model',
  // 5f: integrated_project_ledger renders in initialize too
  // (integrated_project_ledger.js:28 → render :31-37, which clears the
  // .report-tabs-preloader) — the filter_data pattern.
  'integrated_project_ledger',
  // 5f (builder B): all initialize-rendered too — the CA trio
  // (ca_summary.js:32-39, ca_activities.js:30-36, ca_materials.js:30-36),
  // open_projects (:23-25), timeline (:28-31) and order_acknowledgment
  // (:23-25); each render() clears the .report-tabs-preloader synchronously
  // at construction. project_list is deliberately NOT here: its render() is
  // DEFERRED to the initialize columns fetch's .then (project_list.js:34-45)
  // — the pane stays empty until that fetch resolves.
  'ca_summary',
  'ca_activities',
  'ca_materials',
  'open_projects',
  'timeline',
  'order_acknowledgment',
  // 5f (builder C): the 13 stub tabs are ALL initialize-rendered too — the
  // same 39-41 line view skeleton (initialize → render() clears the
  // .report-tabs-preloader synchronously, e.g. mech_dwg.js:22-31); no
  // Dispatcher listeners at all, so a generate does nothing beyond the pane
  // construction (StubReportTabs.tsx).
  'mech_dwg',
  'temperature_profile',
  'eng_hours',
  'plant_hours',
  'mfg_package',
  'base_cut_list',
  'floor_cut_list',
  'casing_wall_cut_list',
  'roof_cut_list',
  'belt_drive_select',
  'parts_by_others_list',
  'freight_request',
  'bill_of_lading',
]);

/**
 * 5d: tabs whose 'open:generated_report:<name>' handler is LIVE — re-opening
 * the report re-renders the cached result and removes the empty board
 * (phx_selection.js:166-174, ahu_model.js:83-92 → action.js:60-64).
 * er_wheel_selection's handler is fully commented out
 * (er_wheel_selection.js:168-177), so it is NOT here; for every other tab
 * `this.data[state]` is never populated by live code, so `generated` stays
 * false and the empty board re-activates — matching the page's default.
 */
export const REOPENABLE_TABS = new Set([
  'phx_selection',
  'ahu_model',
  // 5e: the order tabs' handlers are LIVE (sales_order.js:74-83,
  // service_order.js:65-74, shipping_order.js:110-118) and their cache IS
  // populated by the live socket leg — keyed by report+project only
  // (reportCacheKey), so a reopen with a different AHU selection still hits.
  'sales_order',
  'service_order',
  'shipping_order',
  // 5f: submittal_package's handler is LIVE (submittal.package.js:375-385)
  // and its cache IS populated by the LIVE (uncommented) generate socket
  // wait (:452-470) — keyed by report+project only (:377,437 →
  // reportCacheKey). renderReport is a no-op (body fully commented, :76-102),
  // so the reopen shows the STALE static shell (faded preloader) — the pane
  // renders exactly that in reopen mode.
  'submittal_package',
]);

/** The shared shell of the Engineering-group tabs (_2d_drawing.tmpl /
 *  pressure_drop_calc.tmpl / electrical_calc.tmpl / weight_calc.tmpl /
 *  center_of_gravity.tmpl — identical except for the structure div). */
interface ShellProps extends GeneratedTabPaneProps {
  group: string;
  /** Structure div under the preloader container (absent in _2d_drawing.tmpl). */
  structureId?: string;
  structureClass?: string;
}

const GeneratedTabShell = ({
  report,
  faded,
  rendered,
  group,
  structureId,
  structureClass,
}: ShellProps) => (
  // Wrapper = the static pane div (main.tmpl:47-89, class="col-xs-12") plus
  // the 'active' openReport adds at generate time (action.js:84) — 'active'
  // supplies the pane's full height, col-xs-12 the grid gutter; dropping
  // either collapses/unframes the pane.
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    {rendered && (
      <>
        <div className="container-header clearfix">
          <h3 className="left">
            {/* sectionName = the .current_report menu button's text, i.e. the
                report display name (e.g. _2d_drawing.js:44-45). */}
            <span className="group-name" tooltip={` : ${report.name}`}>
              {group}
            </span>
            <span className="report-name">{report.name}</span>
          </h3>
        </div>
        <div className="report-content col-xs-12">
          <div className="content">
            <div className="content-item full-height" style={{ overflow: 'auto' }}>
              <div
                className={`preloader-container generation-in-progress${
                  faded ? ' opacity-0' : ''
                }`}
              >
                <div className="preloader active">Report generation in progress...</div>
              </div>
              {structureId && <div id={structureId} className={structureClass} />}
            </div>
          </div>
        </div>
      </>
    )}
  </div>
);

/** The six 2D drawings — ONE component like the one shared legacy view;
 *  _2d_drawing.tmpl has NO structure div. */
const TwoDDrawingTab = (props: GeneratedTabPaneProps) => (
  <GeneratedTabShell {...props} group="Engineering" />
);

const PressureDropCalcTab = (props: GeneratedTabPaneProps) => (
  <GeneratedTabShell
    {...props}
    group="Engineering"
    structureId="pressure_drop_calc_structure"
    structureClass="row full-height"
  />
);

const ElectricalCalcTab = (props: GeneratedTabPaneProps) => (
  <GeneratedTabShell
    {...props}
    group="Engineering"
    structureId="electrical_calc_structure"
    structureClass="full-height"
  />
);

const WeightCalcTab = (props: GeneratedTabPaneProps) => (
  <GeneratedTabShell
    {...props}
    group="Engineering"
    structureId="weight_structure"
    structureClass="full-height"
  />
);

const CenterOfGravityCalcTab = (props: GeneratedTabPaneProps) => (
  <GeneratedTabShell
    {...props}
    group="Engineering"
    structureId="center_of_gravity_structure"
    structureClass="full-height"
  />
);

/** AHU Acoustics report — same generate-only shape as Center of Gravity: no
 *  in-tab result render, the finished PDF (or zip for 2+ AHUs) arrives via
 *  the header notifications poll. */
const AhuAcousticsTab = (props: GeneratedTabPaneProps) => (
  <GeneratedTabShell
    {...props}
    group="Engineering"
    structureId="ahu_acoustics_structure"
    structureClass="full-height"
  />
);

/** bill_of_materials.tmpl:1-18 — container-header carries col-xs-12 and nests
 *  another col-xs-12 div; the report-name text has a leading space. */
const BillOfMaterialsTab = ({ report, faded, rendered }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    {rendered && (
      <>
        <div className="container-header clearfix col-xs-12">
          <div className="col-xs-12">
            <h3 className="left">
              <span className="group-name" tooltip={` : ${report.name}`}>
                Purchasing
              </span>
              <span className="report-name"> {report.name}</span>
            </h3>
          </div>
        </div>
        <div className="report-content col-xs-12">
          <div className="content">
            <div className="content-item full-height" style={{ overflow: 'auto' }}>
              <div
                className={`preloader-container generation-in-progress${
                  faded ? ' opacity-0' : ''
                }`}
              >
                <div className="preloader active">Report generation in progress...</div>
              </div>
              <div id="bill_of_materials_structure" className="row full-height" />
            </div>
          </div>
        </div>
      </>
    )}
  </div>
);

/** mfs_profile_cut_list.tmpl:1-13 — its own shell: "Manufacturing " group
 *  (trailing space, tooltip WITHOUT the leading space), an empty .time-stamp
 *  span (only ever filled by the dead renderReport path), and a bare
 *  .dotted-preloader instead of the generation-in-progress container — so the
 *  4.5s fade has nothing to fade here, exactly like legacy. */
const MfsProfileCutListTab = ({ report, rendered }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    {rendered && (
      <>
        <div className="container-header clearfix">
          <h3 className="left">
            <span className="group-name" tooltip={`: ${report.name}`}>
              Manufacturing{' '}
            </span>
            <span className="report-name">{report.name}</span> <span className="time-stamp" />
          </h3>
        </div>
        <div className="col-xs-12 content-container">
          <div className="content">
            <div className="content-item col-xs-12">
              <div className="preloader dotted-preloader active" />
            </div>
          </div>
        </div>
      </>
    )}
  </div>
);

/**
 * ---- Phase 5d — the data-table report family ----
 *
 * unit_technical_data / coil_data / gas_heater_data / evap_pack_data are
 * generate-only tabs exactly like the 5c set: their views POST and, on
 * success, re-render the SAME static shell (unit_technical_data.js:57-100,
 * coil_data.js:49-83, gas_heater_data.js:38-69, evap_pack_data.js:49-84);
 * every in-tab render/export/socket path is dead:
 *  - the generate socket blocks are commented out (coil_data.js:68-79,
 *    gas_heater_data.js:56-67, evap_pack_data.js:69-80;
 *    unit_technical_data.js:114-171 export_pdf/viewPDF fully commented).
 *  - export_csv (unit_technical_data.js:173-220 — the Promise.all([socket,
 *    POST]) → download-click leg at :197-219 — coil_data.js:96-155,
 *    evap_pack_data.js:97-144) is only reachable through the header's
 *    'click #export_csv' (main_header.js:62 → :670-684 →
 *    'export_csv:report:<name>', action.js:226-229) and
 *    tmpl/main_header.tmpl renders NO #export_csv/#print/#export_pdf button
 *    (only #generate_report, main_header.tmpl:45) — the chain is
 *    unreachable. It would also throw first thing:
 *    `self.data[self.current_state].data.generate_id` reads a cache only
 *    the commented-out blocks ever populate. The UTD leg is nevertheless
 *    ported faithfully as exportUnitTechnicalDataCsv (generateReport.ts) —
 *    see the note there.
 *  - export_pdf via 'print:report:coil_data'/'export_pdf:report:coil_data'
 *    (coil_data.js:26-27,157-196, evap_pack_data.js:26-27,146-185) hangs off
 *    the header print()/export_pdf paths; Ctrl+P (main_header.js:128-132)
 *    can reach print() but that whole page-level print harness is 5e scope
 *    (see the ReportsBoardPage note) — and it, too, reads the never-written
 *    `self.data` cache.
 *  - the rest_report data feeders exist server-side
 *    (`/api/v1.0/reports/gas_heater_data/<ahu>` and `evap_pack_data/<ahu>`,
 *    api/rest_report/urls.py:23-28) and the tab folders even ship Backbone
 *    collections for them (tabs/gas_heater_data/collection/all.js,
 *    tabs/evap_pack_data/collection/all.js) — but NO view requires those
 *    collections (grep: only ewheel/ahu_model collections are required), so
 *    the live tabs never call the feeders and render no in-tab table.
 *
 * filter_data has no listeners at all — its view renders the static shell
 * once, in initialize (filter_data.js:22-33; no POST, no data, and the
 * template's time-stamp keeps the hardcoded 2017 placeholder because
 * nothing ever calls setTimeStamp). The stray console.log('Hello
 * filter_data') (:31) is legacy debug noise, not ported.
 *
 * er_wheel_selection / phx_selection / ahu_model have LIVE in-tab renders —
 * see EwheelSelectionTab / PhxSelectionTab / AhuModelTab.
 */

const UnitTechnicalDataTab = (props: GeneratedTabPaneProps) => (
  // unit_technical_data.tmpl:1-16 — structure div is "full-height" (no row).
  <GeneratedTabShell {...props} group="Engineering" structureId="utd_structure" structureClass="full-height" />
);

const CoilDataTab = (props: GeneratedTabPaneProps) => (
  // coil_data.tmpl:1-16.
  <GeneratedTabShell {...props} group="Engineering" structureId="coil_structure" structureClass="row full-height" />
);

const GasHeaterDataTab = (props: GeneratedTabPaneProps) => (
  // gas_heater_data.tmpl:1-16.
  <GeneratedTabShell {...props} group="Engineering" structureId="gas_heater_structure" structureClass="row full-height" />
);

const EvapPackDataTab = (props: GeneratedTabPaneProps) => (
  // evap_pack_data.tmpl:1-16.
  <GeneratedTabShell {...props} group="Engineering" structureId="evap_pack_structure" structureClass="row full-height" />
);

/** filter_data.tmpl:1-9 — tooltip without leading space, report-name WITH a
 *  leading space, hardcoded 2017 time-stamp, bare content (no preloader, no
 *  report-content wrapper). Renders as soon as the pane mounts (initialize →
 *  render, filter_data.js:22-24) — `rendered` is ignored. */
const FilterDataTab = ({ report }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <div className="container-header clearfix">
      <h3 className="left">
        <span className="group-name" tooltip={`: ${report.name}`}>
          Engineering
        </span>
        <span className="report-name"> {report.name}</span>
        <span className="time-stamp">Generated on: 2017.09.13 19:10:46</span>
      </h3>
    </div>
    <div className="content">
      <div className="content-item" />
    </div>
  </div>
);

/**
 * value → pane. Values are the legacy report_btn values (the six drawing
 * aliases from report/mvc/app.js:170-176 plus the five calc/list reports,
 * plus the 5d data-table family). Reports not in this map keep the generic
 * 5b GeneratingReportTab.
 */
export const GENERATED_TAB_PANES: Record<string, PaneComponent> = {
  plan: TwoDDrawingTab,
  plan_and_elevation_ns: TwoDDrawingTab,
  elevation_near_side: TwoDDrawingTab,
  elevation_far_side: TwoDDrawingTab,
  elevation_intake: TwoDDrawingTab,
  elevation_discharge: TwoDDrawingTab,
  // ⚠ dev-sync G / CB-3 — VERIFIED BY INSPECTION ONLY, NEVER EXECUTED.
  // "Fan + Motor + Isolator Assembly" is the seventh alias onto the shared 2D
  // drawing view (fmi_assembly.js — a 3-line
  // `define(['_2d_drawing.core'], …)` wrapper, + report/mvc/app.js:177).
  // UNTESTABLE on dev3: there is no `ksdm_master_ahu_report` row for
  // value='fmi_assembly' (and no migration in the delta creating one), so the
  // report button never renders and this entry can never be reached. Ported
  // anyway so the tab works the day the master row is seeded, instead of
  // silently falling through to the generic GeneratingReportTab shell.
  fmi_assembly: TwoDDrawingTab,
  pressure_drop_calc: PressureDropCalcTab,
  electrical_calc: ElectricalCalcTab,
  weight_calc: WeightCalcTab,
  center_of_gravity_calc: CenterOfGravityCalcTab,
  ahu_acoustics: AhuAcousticsTab,
  bill_of_materials: BillOfMaterialsTab,
  mfs_profile_cut_list: MfsProfileCutListTab,
  // ---- 5d ----
  unit_technical_data: UnitTechnicalDataTab,
  coil_data: CoilDataTab,
  gas_heater_data: GasHeaterDataTab,
  evap_pack_data: EvapPackDataTab,
  filter_data: FilterDataTab,
  er_wheel_selection: EwheelSelectionTab,
  phx_selection: PhxSelectionTab,
  ahu_model: AhuModelTab,
  // fan_data: importing FanDataTab also registers the REAL curve-image
  // provider with the kit (module-scope registerCurveImageProvider) — the
  // multi-AHU batched ZIP and submittal img[] payloads carry real curves.
  fan_data: FanDataTab,
  // ---- 5e (builder A) — document + project-scope generate-only tabs; all
  // render their shell on POST success like the 5c family (liveness analysis
  // + template quirks documented in DocumentReportTabs.tsx). ----
  sales_terms: SalesTermsTab,
  shipping_terms: CmsDocumentTab,
  warranty_terms: CmsDocumentTab,
  ahu_guide_specs: CmsDocumentTab,
  condensing_unit_guide_specs: CmsDocumentTab,
  packaged_unit_guide_specs: CmsDocumentTab,
  submittal_cover: SubmittalCoverTab,
  scope_of_supply: ScopeOfSupplyTab,
  equipment_list: EquipmentListTab,
  equipment_schedule: EquipmentScheduleTab,
  pricing: PricingTab,
  // ---- 5e (builder B) — the order family (LIVE POST→socket→in-tab renders,
  // reopenable via the 2-part cache key) + the proposal generate-only shell
  // (liveness analysis + template quirks documented in OrderReportTabs.tsx).
  sales_order: SalesOrderTab,
  service_order: ServiceOrderTab,
  shipping_order: ShippingOrderTab,
  proposal: ProposalTab,
  // ---- 5f (builder A) — submittal package (generate-only shell + the one
  // stale-shell reopen), usage statistics (generate-only shell) and the
  // integrated project ledger (initialize-rendered shell + socket-stripped
  // in-tab preloader; ZIP delivery is the kit's). Liveness analysis +
  // template quirks documented in SubmittalUsageLedgerTabs.tsx. ----
  submittal_package: SubmittalPackageTab,
  usage_statistics: UsageStatisticsTab,
  // Dev-sync I — the coil-analytics generate-only shell (usage_statistics
  // pattern: POST-success render, no reopen — SubmittalUsageLedgerTabs.tsx).
  coil_analytics: CoilAnalyticsTab,
  integrated_project_ledger: IntegratedProjectLedgerTab,
  // ---- 5f (builder B) — the Cost Accounting trio (initialize-rendered
  // shells whose generate POST re-renders the same static shell; the
  // report.ahu/report.project result tables are dead — liveness analysis in
  // CostAccountingTabs.tsx) and the Management/Schedule family: project_list
  // (once-per-page columns+projects fetch with the accumulated-row quirk),
  // open_projects / order_acknowledgment (static placeholder panes) and
  // timeline (per-generate three-collection fetch → task rows). None are
  // reopenable; none of the four Management/Schedule values has a
  // ksdm_master_ahu_report row on dev3 (panes work when granted —
  // ManagementScheduleTabs.tsx). ----
  ca_summary: CaSummaryTab,
  ca_activities: CaActivitiesTab,
  ca_materials: CaMaterialsTab,
  project_list: ProjectListTab,
  open_projects: OpenProjectsTab,
  timeline: TimelineTab,
  order_acknowledgment: OrderAcknowledgmentTab,
  // ---- 5f (builder C) — the 13 stub tabs (static placeholder shells; no
  // listeners, no requests — liveness analysis + template quirks documented
  // in StubReportTabs.tsx). None of the 13 values has a
  // ksdm_master_ahu_report row on dev3 (panes work when granted).
  // uvc_selection is NOT here: no 'view.uvc_selection' RequireJS alias
  // exists (app.js:55-224), so ensureReportView (action.js:197-206) can
  // never build it — dead by construction, nothing to port. ----
  mech_dwg: MechDwgTab,
  temperature_profile: TemperatureProfileTab,
  eng_hours: EngHoursTab,
  plant_hours: PlantHoursTab,
  mfg_package: MfgPackageTab,
  base_cut_list: BaseCutListTab,
  floor_cut_list: FloorCutListTab,
  casing_wall_cut_list: CasingWallCutListTab,
  roof_cut_list: RoofCutListTab,
  belt_drive_select: BeltDriveSelectTab,
  parts_by_others_list: PartsByOthersListTab,
  freight_request: FreightRequestTab,
  bill_of_lading: BillOfLadingTab,
};
